import { useState, useEffect, useCallback, useRef } from 'react'
import toast from 'react-hot-toast'
import apiClient from '@/lib/api'
import {
  addSubmission,
  getAllSubmissions,
  removeSubmission,
  getPendingCount,
} from '@/lib/offlineDb'

interface UseOfflineSyncReturn {
  isOnline: boolean
  pendingCount: number
  syncNow: () => Promise<void>
  lastSyncTime: string | null
  /** Queue a submission payload for later sync (or send immediately if online). Returns true if sent online. */
  submitOrQueue: (payload: unknown) => Promise<boolean>
}

export function useOfflineSync(): UseOfflineSyncReturn {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine)
  const [pendingCount, setPendingCount] = useState<number>(0)
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null)
  const isSyncing = useRef(false)

  // ---- Refresh the pending count from IndexedDB ----
  const refreshPendingCount = useCallback(async () => {
    try {
      const count = await getPendingCount()
      setPendingCount(count)
    } catch {
      // IndexedDB may be unavailable in certain contexts; fail silently
    }
  }, [])

  // ---- Replay all queued submissions in order ----
  const syncNow = useCallback(async () => {
    if (isSyncing.current) return
    if (!navigator.onLine) return
    isSyncing.current = true

    try {
      const submissions = await getAllSubmissions()
      if (submissions.length === 0) {
        isSyncing.current = false
        return
      }

      let synced = 0
      for (const submission of submissions) {
        try {
          await apiClient.post('/api/v1/availability', submission.payload)
          await removeSubmission(submission.id!)
          synced++
        } catch {
          // Stop replaying on first failure to preserve order
          break
        }
      }

      if (synced > 0) {
        const now = new Date().toISOString()
        setLastSyncTime(now)
        toast.success(
          synced === 1
            ? '1 déclaration synchronisée avec succès'
            : `${synced} déclarations synchronisées avec succès`
        )
      }

      await refreshPendingCount()
    } finally {
      isSyncing.current = false
    }
  }, [refreshPendingCount])

  // ---- Online / offline listeners ----
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      // Auto-replay queued submissions when connectivity returns
      void syncNow()
    }
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [syncNow])

  // ---- Load initial pending count on mount ----
  useEffect(() => {
    void refreshPendingCount()
  }, [refreshPendingCount])

  // ---- Submit or queue ----
  const submitOrQueue = useCallback(
    async (payload: unknown): Promise<boolean> => {
      if (navigator.onLine) {
        try {
          await apiClient.post('/api/v1/availability', payload)
          setLastSyncTime(new Date().toISOString())
          return true
        } catch {
          // Network request failed even though browser says online.
          // Fall through to queue the payload.
        }
      }

      // Offline (or request failed): queue in IndexedDB
      await addSubmission(payload)
      await refreshPendingCount()
      return false
    },
    [refreshPendingCount]
  )

  return { isOnline, pendingCount, syncNow, lastSyncTime, submitOrQueue }
}
